import React, { useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Edit, Trash2, Plus, Search, ChevronLeft, ChevronRight, BarChart3, Grid3X3, Phone, User as UserIcon, Stethoscope, FileText } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as PieTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as BarTooltip, Legend } from 'recharts';
import { useNotification } from '../../contexts/NotificationContext';
import { AuthContext } from '../../contexts/AuthContext';
import { useFeedbackList } from './shared/services/useFeedbackService';

const ROWS_PER_PAGE = 5;

const FeedbackListPage = () => {
  const { userRole, permissions } = useContext(AuthContext);
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '0', 10);
  const [viewGraph, setViewGraph] = useState(false);
  
  const { feedbacks, loading, deleteFeedback } = useFeedbackList();

  const hasPermission = (perm) => (userRole || '').toLowerCase() === 'admin' || (permissions || []).includes(perm);
  const showActionsColumn = hasPermission('Patient Feedback Delete');

  const filtered = feedbacks.filter(fb =>
    (fb.patient_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (fb.mobile_no || "").toString().includes(searchTerm) ||
    (fb.uhid_no || "").toString().includes(searchTerm) ||
    (fb.doctor_name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const paginated = filtered.slice(currentPage * ROWS_PER_PAGE, (currentPage + 1) * ROWS_PER_PAGE);

  // Graph Data Processing
  const getFeedbackReasonsData = () => {
    const counts = { 'High Cost': 0, 'No Insurance Available': 0, 'Lack of Support': 0, 'Other': 0 };
    filtered.forEach(fb => {
      if (counts[fb.feedback_reason] !== undefined) counts[fb.feedback_reason]++;
      else counts['Other']++;
    });
    return Object.keys(counts).map(key => ({ name: key, value: counts[key] }));
  };

  const getNegativeFeedbackStats = () => {
    const negativeReasons = ['High Cost', 'No Insurance Available', 'Lack of Support'];
    const doctorAttendance = {};
    let totalNegative = 0;
    
    filtered.forEach(fb => {
      if (negativeReasons.includes(fb.feedback_reason)) {
        totalNegative++;
        doctorAttendance[fb.doctor_name] = (doctorAttendance[fb.doctor_name] || 0) + 1;
      }
    });

    return [
      { name: 'Doctors with Negative Feedback', count: Object.keys(doctorAttendance).length },
      { name: 'Total Negative Feedbacks', count: totalNegative }
    ];
  };

  const handleDelete = (fb) => {
    showNotification('confirm', `Are you sure you want to delete feedback from ${fb.patient_name}?`, async () => {
      try {
        await deleteFeedback(fb.id);
        showNotification('success', 'Feedback deleted successfully');
      } catch (err) {
        showNotification('error', 'Failed to delete feedback');
      }
    });
  };

  if (loading) return (
    <div className="w-full max-w-6xl mx-auto p-4 animate-pulse space-y-4">
      <div className="h-12 bg-gray-200 rounded"></div>
      {[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-gray-200 rounded"></div>)}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
      {/* Action bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="relative w-full lg:w-2/3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search by patient, mobile, UHID, or doctor..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setSearchParams({ page: '0' }); }}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none transition-all"
            />
          </div>
          <div className="flex gap-3 w-full lg:w-auto">
            {hasPermission('Patient Feedback Add') && (
              <button 
                onClick={() => navigate('/addFeedback', { state: { activeMenu: 'Patient Feedback', page: currentPage } })} 
                className="flex items-center gap-2 bg-teal-600 text-white px-4 py-3 rounded-lg hover:bg-teal-700 font-medium flex-1 lg:flex-none justify-center"
              >
                <Plus className="h-4 w-4" /> Add Feedback
              </button>
            )}
            <button onClick={() => setViewGraph(!viewGraph)} className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-200 border border-gray-300 font-medium flex-1 lg:flex-none justify-center">
              {viewGraph ? <><Grid3X3 className="h-4 w-4" /> Table View</> : <><BarChart3 className="h-4 w-4" /> Graph View</>}
            </button>
          </div>
        </div>
      </div>

      {viewGraph ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-teal-900 text-center mb-6">Feedback Distribution</h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={getFeedbackReasonsData()} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} fill="#8884d8">
                    {[ '#ff8042', '#0088FE', '#00C49F', '#FFBB28' ].map((c, i) => <Cell key={i} fill={c} />)}
                  </Pie>
                  <PieTooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-teal-900 text-center mb-6">Negative Feedback Metrics</h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={getNegativeFeedbackStats()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <BarTooltip />
                  <Bar dataKey="count" fill="#ee6666" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-teal-50 border-b border-teal-100">
                  {['S/No', 'Patient Name', 'Mobile', 'UHID No', 'Feedback', 'Date', 'Doctor', showActionsColumn && 'Actions'].filter(Boolean).map(h => (
                    <th key={h} className="text-left p-4 font-semibold text-teal-900">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr><td colSpan={showActionsColumn ? 8 : 7} className="p-12 text-center text-gray-500">No feedback found</td></tr>
                ) : paginated.map((fb, i) => (
                  <tr key={fb.id} className={`border-b border-gray-100 hover:bg-teal-50/50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                    <td className="p-4">{currentPage * ROWS_PER_PAGE + i + 1}</td>
                    <td className="p-4 font-medium">{fb.patient_name}</td>
                    <td className="p-4"><div className="flex items-center gap-2 text-gray-600"><Phone className="h-4 w-4" />{fb.mobile_no}</div></td>
                    <td className="p-4">{fb.uhid_no}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${fb.feedback_reason === 'High Cost' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                        {fb.feedback_reason}
                      </span>
                    </td>
                    <td className="p-4 text-gray-600">{fb.feedback_date}</td>
                    <td className="p-4"><div className="flex items-center gap-2 text-gray-600"><Stethoscope className="h-4 w-4" />{fb.doctor_name}</div></td>
                    {showActionsColumn && (
                      <td className="p-4">
                        <button 
                          onClick={() => handleDelete(fb)} 
                          className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors font-medium text-sm" 
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4">
            {paginated.map((fb, i) => (
              <div key={fb.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium bg-teal-100 text-teal-800 px-2 py-1 rounded">#{currentPage * ROWS_PER_PAGE + i + 1}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${fb.feedback_reason === 'High Cost' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>{fb.feedback_reason}</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">{fb.patient_name}</h3>
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                   <div className="flex items-center gap-2"><Phone className="h-4 w-4" />{fb.mobile_no}</div>
                   <div className="flex items-center gap-2"><UserIcon className="h-4 w-4" />UHID: {fb.uhid_no}</div>
                   <div className="flex items-center gap-2"><Stethoscope className="h-4 w-4" />Dr. {fb.doctor_name}</div>
                   <div className="flex items-center gap-2"><FileText className="h-4 w-4" />{fb.feedback_date}</div>
                </div>
                {showActionsColumn && (
                  <button 
                    onClick={() => handleDelete(fb)} 
                    className="w-full flex items-center justify-center gap-2 py-2.5 text-red-600 border border-red-200 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Feedback
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      {filtered.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">Showing {currentPage * ROWS_PER_PAGE + 1} to {Math.min((currentPage + 1) * ROWS_PER_PAGE, filtered.length)} of {filtered.length} entries</div>
          <div className="flex gap-2">
            <button onClick={() => setSearchParams({ page: '0' })} disabled={currentPage === 0} className="p-2 border rounded-lg disabled:opacity-50 text-xs font-bold uppercase tracking-widest">First</button>
            <button onClick={() => setSearchParams({ page: Math.max(0, currentPage - 1).toString() })} disabled={currentPage === 0} className="p-2 border rounded-lg disabled:opacity-50"><ChevronLeft className="h-4 w-4" /></button>
            <span className="px-4 py-2 bg-gray-50 rounded-lg text-sm font-medium">{currentPage + 1} of {totalPages}</span>
            <button onClick={() => setSearchParams({ page: Math.min(totalPages - 1, currentPage + 1).toString() })} disabled={currentPage >= totalPages - 1} className="p-2 border rounded-lg disabled:opacity-50"><ChevronRight className="h-4 w-4" /></button>
            <button onClick={() => setSearchParams({ page: (totalPages - 1).toString() })} disabled={currentPage >= totalPages - 1} className="p-2 border rounded-lg disabled:opacity-50 text-xs font-bold uppercase tracking-widest">Last</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackListPage;
